// ---- the dishes ----
// A dish is a static prop: no rig, no clips, no mixer, nothing to drive. It is
// the simplest thing the engine's Body can be — a model, a few collision
// spheres, how far it spreads, and when it arrived — and every bit of the
// behaviour lives in the two people instead. That is the whole reason the food
// could be bought, or generated, rather than rigged.
//
// Sized by FOOTPRINT, not by height. See the note on `across` in data.ts: a
// wedge of watermelon and a bowl of tabbouleh scaled to the same height are not
// the same size in any sense a three-year-old would recognise.
//
// Sitting on the TABLE, not the grass: a flight comes down to the top's height
// (throwDish takes it), and the field puts every landed dish back at it
// after the engine's shuffle (updateField, field.ts).
import * as THREE from 'three';
import {GLTFLoader} from 'three/addons/loaders/GLTFLoader.js';
import {asset} from '../../core/assets';
import {addCollider,footprint,isMesh} from '../../core/body';
import {scene} from '../../core/scene';
import {backOut,clamp} from '../../core/util';
import {pack,type FoodSpec} from './pack';
import type {Body} from '../../core/types';

export interface Dish extends Body{spec:FoodSpec;
  // Her hands, then the air, then the table. It is in the field only on the
  // last of those: no collisions, nobody walks to it, and it has not "arrived"
  // yet. fly is null once it has landed.
  fly:Flight|null;
  // He has had it. It stays where it is — a sofra is what has been served, not
  // what is left — but nobody comes for it again, and it is the first to go
  // when the table is full (field.ts).
  eaten:boolean;
  gone:number;                                   // when it started leaving the table; 0 while it is on it
  // ...and he has picked it up. A dish in his hands is off the table for as
  // long as the mouthful lasts: the field's shuffle does not push it about in
  // mid-air and the clamp does not snap it back down to the top, because for
  // those three and a half seconds where it is is HIS to say (boy.ts). It is
  // still in the field, so it still counts against the table's ten and nothing
  // is thrown onto the spot it is coming back to.
  held:boolean;
  home:THREE.Vector3;                            // and the spot on the top it is coming back to, while it is up
}
// The two halves of a serve. `hands` is read every frame of the hold rather
// than once at the start, because she is TURNING while she holds it: a point
// taken up front would leave the dish hanging where her hands used to be. `o`
// is where they finally let go, taken on the first frame past t0. `y1` is the
// height it comes down to — the table's top, not the ground.
interface Flight{hands:()=>THREE.Vector3;at:number;t0:number;ms:number;spin:number;x1:number;y1:number;z1:number;rise:number;o:THREE.Vector3|null}
// How long it sits in her hands before it goes — long enough for her to have
// turned to face the throw (her heading eases at 4/s, so most of a quarter
// turn is done in half a second) and for a child to see what she has made.
export const HOLD=700, FLIGHT=760;
export const tpl:Record<string,THREE.Object3D>={};
export function loadDishes(){
  for(const f of pack().FOODS)new GLTFLoader().loadAsync(asset(f.file)).then(g=>{tpl[f.w]=g.scene;})
    .catch(err=>console.warn(f.file+' did not load',err));
}
export const ready=(w:string)=>!!tpl[w];

export function makeDish(spec:FoodSpec):Dish{
  const d=new THREE.Group() as Dish, m=tpl[spec.w].clone(true);
  m.updateMatrixWorld(true);
  const box=new THREE.Box3().setFromObject(m), sz=box.getSize(new THREE.Vector3());
  const s=spec.across/Math.max(sz.x,sz.z);          // the footprint rule
  m.scale.setScalar(s); m.position.y=-box.min.y*s;  // sitting on whatever it lands on, not floating over it
  m.traverse(o=>{if(isMesh(o)){o.castShadow=true;o.receiveShadow=true;o.userData.ele=d;}});
  d.add(m);
  Object.assign(d,{w:spec.w,spec,cols:[],pad:0,full:1,born:0,fly:null,eaten:false,gone:0,held:false,home:new THREE.Vector3()});
  d.pad=footprint(d);
  // One sphere is enough for a plate: it is round, it is flat, and nothing about
  // it moves. A creature gets three down its length because it is long and
  // turns; a bowl is a bowl from every angle.
  addCollider(d,0,sz.y*s*0.5,0,spec.across*0.5);
  scene.add(d); return d;
}
// The serve. It pops into being in her hands, rides them while she turns to
// face where it is going, and only then leaves — a parabola over `ms`, turning
// as it goes, because a dish that slid there would read as a bug and a dish
// that appeared there would not read as HERS. The hold is the whole point of
// the moment: a throw nobody saw her wind up for is just food arriving.
export function throwDish(d:Dish,hands:()=>THREE.Vector3,to:{x:number;y:number;z:number},t:number){
  const h=hands(); d.position.copy(h);
  d.scale.setScalar(0.01);
  // How high it goes is how far it has to travel. She stands a stride or two
  // off the table and the spot is anywhere on it, so the same throw can be one
  // stride or three, and a fixed arc makes the short one a lob over her own
  // head. Lower than it was when the food went to the ground: the table meets
  // it most of the way, and a lob to a table at her thigh is a toss, not a
  // shot put.
  const reach=Math.hypot(to.x-h.x,to.z-h.z);
  d.fly={hands,at:t,t0:t+HOLD,ms:FLIGHT,x1:to.x,y1:to.y,z1:to.z,rise:clamp(0.35+0.2*reach,0.55,0.95),
    spin:Math.random()<0.5?-1:1,o:null};
}
// true once it has come down; the caller is what admits it to the field
export function updateFlight(d:Dish,t:number){
  const f=d.fly; if(!f)return false;
  if(t<f.t0){                                        // still hers: in her hands, turning with her
    d.position.copy(f.hands());
    d.scale.setScalar(d.full*Math.max(0.02,backOut(Math.min(1,(t-f.at)/260))));   // she made it: it grows into her hands
    d.rotation.y+=0.02;
    return false;
  }
  if(!f.o){f.o=f.hands().clone();d.scale.setScalar(d.full);}   // let go, from wherever her hands ended up
  const k=Math.min(1,(t-f.t0)/f.ms);
  d.position.x=f.o.x+(f.x1-f.o.x)*k;
  d.position.z=f.o.z+(f.z1-f.o.z)*k;
  d.position.y=f.o.y+(f.y1-f.o.y)*k+f.rise*Math.sin(Math.PI*k);   // up and over, coming down onto the table
  d.rotation.y+=f.spin*0.09;
  if(k<1)return false;
  d.position.y=f.y1; d.rotation.y=Math.random()*6.28; d.fly=null;
  return true;
}
